/**
 * Memory tracker — lifecycle counters + on-screen overlay + console logging.
 *
 * Activated only when URL contains `?mem-debug=1`. No-op otherwise (all exports
 * become cheap guarded calls). Goal: diagnose OOM on iPhone Safari when user
 * repeatedly opens/closes inside GLB scenes.
 *
 * Usage:
 *   incrementWebGLContext('glb' | 'splat')
 *   decrementWebGLContext('glb' | 'splat')
 *   incrementScene(kind)
 *   decrementScene(kind)
 *   registerMixer() / unregisterMixer()
 *   registerPoiManager() / unregisterPoiManager()
 *   registerPreviewModel() / unregisterPreviewModel()
 *   logEvent(name, data?)
 *   snapshot() → reads perf.memory + counters
 */

type SceneKind = 'glb' | 'splat' | 'globalMap' | 'main';

type PerformanceWithMemory = Performance & {
  memory?: {
    usedJSHeapSize: number;
    totalJSHeapSize: number;
    jsHeapSizeLimit: number;
  };
};

interface Counters {
  activeWebGLContexts: number;
  activeGlbScenes: number;
  activeSplatScenes: number;
  activePoiManagers: number;
  activeMixers: number;
  activePreviewModels: number;
  totalRenderersCreated: number;
  totalScenesDisposed: number;
  poiArtifactOpenCount: number;
  poiArtifactCloseCount: number;
  webglContextLostCount: number;
  webglContextRestoredCount: number;
}

export interface MemorySnapshot extends Counters {
  usedJSHeapMb: number | null;
  totalJSHeapMb: number | null;
  jsHeapLimitMb: number | null;
  timestampMs: number;
}

const isBrowser = typeof window !== 'undefined';
const enabled = isBrowser && new URLSearchParams(window.location.search).get('mem-debug') === '1';

const counters: Counters = {
  activeWebGLContexts: 0,
  activeGlbScenes: 0,
  activeSplatScenes: 0,
  activePoiManagers: 0,
  activeMixers: 0,
  activePreviewModels: 0,
  totalRenderersCreated: 0,
  totalScenesDisposed: 0,
  poiArtifactOpenCount: 0,
  poiArtifactCloseCount: 0,
  webglContextLostCount: 0,
  webglContextRestoredCount: 0,
};

let overlay: HTMLDivElement | null = null;
let overlayRafId: number | null = null;
let overlayDirty = true;
let snapshotIntervalId: ReturnType<typeof setInterval> | null = null;

const LOG_PREFIX = '[MemoryTracker]';

const getMemoryMb = () => {
  const perf = (isBrowser ? performance : undefined) as PerformanceWithMemory | undefined;
  const mem = perf?.memory;
  if (!mem) {
    return { usedJSHeapMb: null, totalJSHeapMb: null, jsHeapLimitMb: null };
  }
  return {
    usedJSHeapMb: +(mem.usedJSHeapSize / (1024 * 1024)).toFixed(1),
    totalJSHeapMb: +(mem.totalJSHeapSize / (1024 * 1024)).toFixed(1),
    jsHeapLimitMb: +(mem.jsHeapSizeLimit / (1024 * 1024)).toFixed(1),
  };
};

export const snapshot = (): MemorySnapshot => ({
  ...counters,
  ...getMemoryMb(),
  timestampMs: isBrowser ? performance.now() : 0,
});

const safeStringify = (value: unknown): string => {
  try {
    return JSON.stringify(value);
  } catch {
    return String(value);
  }
};

export const logEvent = (name: string, data?: unknown): void => {
  if (!enabled) return;
  const snap = snapshot();
  const payload =
    data !== undefined && data !== null && typeof data === 'object'
      ? { ...(data as Record<string, unknown>), ...snap }
      : data !== undefined
      ? { data, ...snap }
      : snap;
  console.log(`${LOG_PREFIX} ${name} ${safeStringify(payload)}`);
  markOverlayDirty();
};

const markOverlayDirty = (): void => {
  if (!enabled || !overlay) return;
  overlayDirty = true;
  if (overlayRafId !== null) return;
  overlayRafId = requestAnimationFrame(renderOverlay);
};

const renderOverlay = (): void => {
  overlayRafId = null;
  if (!enabled || !overlay || !overlayDirty) return;
  overlayDirty = false;
  const snap = snapshot();
  const lines: string[] = [
    `WebGL: ${snap.activeWebGLContexts}  (lost:${snap.webglContextLostCount} restored:${snap.webglContextRestoredCount})`,
    `Scenes: GLB ${snap.activeGlbScenes}  Splat ${snap.activeSplatScenes}`,
    `PoiMgrs: ${snap.activePoiManagers}`,
    `Mixers: ${snap.activeMixers}`,
    `PreviewModels: ${snap.activePreviewModels}`,
    snap.usedJSHeapMb !== null
      ? `Heap: ${snap.usedJSHeapMb} / ${snap.totalJSHeapMb} MB  (limit ${snap.jsHeapLimitMb})`
      : 'Heap: n/a',
    `Artifact: open ${snap.poiArtifactOpenCount} / close ${snap.poiArtifactCloseCount}`,
    `Cumulative: rend ${snap.totalRenderersCreated} disp ${snap.totalScenesDisposed}`,
  ];
  overlay.textContent = lines.join('\n');
};

const ensureOverlay = (): void => {
  if (!enabled || overlay || typeof document === 'undefined') return;
  overlay = document.createElement('div');
  Object.assign(overlay.style, {
    position: 'fixed',
    top: '8px',
    right: '8px',
    zIndex: '999999',
    background: 'rgba(0,0,0,0.75)',
    color: '#0f0',
    padding: '6px 10px',
    fontFamily: 'monospace',
    fontSize: '11px',
    lineHeight: '1.4',
    whiteSpace: 'pre',
    borderRadius: '6px',
    pointerEvents: 'none',
    maxWidth: '50vw',
  } as CSSStyleDeclaration);
  overlay.setAttribute('data-mem-debug', '1');
  document.body.appendChild(overlay);
  markOverlayDirty();
};

export const incrementWebGLContext = (kind: SceneKind): void => {
  if (!enabled) return;
  counters.activeWebGLContexts++;
  counters.totalRenderersCreated++;
  logEvent(`webgl context created (${kind})`);
};

export const decrementWebGLContext = (kind: SceneKind): void => {
  if (!enabled) return;
  counters.activeWebGLContexts = Math.max(0, counters.activeWebGLContexts - 1);
  logEvent(`webgl context released (${kind})`);
};

export const incrementScene = (kind: SceneKind): void => {
  if (!enabled) return;
  if (kind === 'glb') counters.activeGlbScenes++;
  else if (kind === 'splat') counters.activeSplatScenes++;
  // 'globalMap' — отдельная категория, не засчитывается в GLB/Splat счётчики
  markOverlayDirty();
};

export const decrementScene = (kind: SceneKind): void => {
  if (!enabled) return;
  if (kind === 'glb') counters.activeGlbScenes = Math.max(0, counters.activeGlbScenes - 1);
  else if (kind === 'splat') counters.activeSplatScenes = Math.max(0, counters.activeSplatScenes - 1);
  counters.totalScenesDisposed++;
  markOverlayDirty();
};

export const registerMixer = (): void => {
  if (!enabled) return;
  counters.activeMixers++;
  markOverlayDirty();
};

export const unregisterMixer = (): void => {
  if (!enabled) return;
  counters.activeMixers = Math.max(0, counters.activeMixers - 1);
  markOverlayDirty();
};

export const registerPoiManager = (): void => {
  if (!enabled) return;
  counters.activePoiManagers++;
  markOverlayDirty();
};

export const unregisterPoiManager = (): void => {
  if (!enabled) return;
  counters.activePoiManagers = Math.max(0, counters.activePoiManagers - 1);
  markOverlayDirty();
};

export const registerPreviewModel = (): void => {
  if (!enabled) return;
  counters.activePreviewModels++;
  markOverlayDirty();
};

export const unregisterPreviewModel = (): void => {
  if (!enabled) return;
  counters.activePreviewModels = Math.max(0, counters.activePreviewModels - 1);
  markOverlayDirty();
};

export const recordPoiArtifactOpen = (): void => {
  if (!enabled) return;
  counters.poiArtifactOpenCount++;
  logEvent('poi artifact opened');
};

export const recordPoiArtifactClose = (): void => {
  if (!enabled) return;
  counters.poiArtifactCloseCount++;
  logEvent('poi artifact closed');
};

export const recordWebglContextLost = (): void => {
  if (!enabled) return;
  counters.webglContextLostCount++;
  logEvent('webglcontextlost event');
};

export const recordWebglContextRestored = (): void => {
  if (!enabled) return;
  counters.webglContextRestoredCount++;
  logEvent('webglcontextrestored event');
};

export const isMemoryDebugEnabled = (): boolean => enabled;

// ─────────────────────────────────────────────────────────────────────
// WeakRef post-dispose retention probe
// ─────────────────────────────────────────────────────────────────────

/**
 * After a scene tear-down, schedule WeakRef checks at progressively longer
 * delays. If `deref()` returns the object after several seconds, that object
 * is being held by a closure / event listener / Effector store / module-level
 * variable and is leaking. iOS JSC is slow to GC, so we deliberately probe
 * up to ~10 seconds out before declaring a leak.
 *
 * NB: WeakRef availability — Safari 14.1+ / iOS 14.5+. Older browsers fall
 * back to a no-op (we can't track without it).
 *
 * Usage in `dispose`:
 *   trackPostDispose('sdkEngine', engine, { sceneId: config.id });
 *
 * Each call schedules `setTimeout(..., delayMs)` for each entry in
 * `RETENTION_PROBE_DELAYS_MS`. The setTimeout closure itself captures only the
 * WeakRef — NOT a strong ref to the target — so the probe never blocks GC.
 */
const RETENTION_PROBE_DELAYS_MS = [1500, 4000, 9000];

/**
 * Reflective inspection of an object's own enumerable + non-enumerable
 * properties. Returns a compact summary of which fields are non-null and
 * what their constructor names are. Used inside `trackPostDispose` at the
 * final probe point — if a target is STILL alive after 9s, we want to know
 * WHICH of its fields are pinning it down (most often: a strong ref to a
 * THREE.WebGLRenderer / Scene / Camera / Material / Worker that some
 * external listener is holding).
 *
 * Limits per object:
 *   - First 80 own property names
 *   - Skip primitives (numbers, strings, booleans) — only objects/arrays/funcs
 *   - For each non-null object value: log its constructor name + (if function)
 *     its name + (if array) its length
 *   - Catches per-property access errors (some fields are getters that throw
 *     once disposed)
 */
const summarizeRetainedFields = (target: object): Array<{ key: string; type: string; extra?: string }> => {
  const out: Array<{ key: string; type: string; extra?: string }> = [];
  let names: string[];
  try {
    names = Object.getOwnPropertyNames(target);
  } catch {
    return out;
  }
  const limit = Math.min(names.length, 80);
  for (let i = 0; i < limit; i++) {
    const key = names[i];
    let value: unknown;
    try {
      value = (target as Record<string, unknown>)[key];
    } catch {
      out.push({ key, type: 'getter-threw' });
      continue;
    }
    if (value === null || value === undefined) continue;
    const t = typeof value;
    if (t !== 'object' && t !== 'function') continue;
    const ctorName = (value as { constructor?: { name?: string } })?.constructor?.name ?? t;
    let extra: string | undefined;
    if (Array.isArray(value)) {
      extra = `len=${value.length}`;
    } else if (t === 'function') {
      extra = `fn:${(value as { name?: string }).name ?? '<anon>'}`;
    } else if ((value as { isTexture?: boolean })?.isTexture) {
      const data = ((value as { source?: { data?: { byteLength?: number } } }).source?.data);
      if (data && typeof data.byteLength === 'number') {
        extra = `${(data.byteLength / (1024 * 1024)).toFixed(1)}MB`;
      }
    } else if ((value as { isObject3D?: boolean })?.isObject3D) {
      const children = (value as { children?: unknown[] }).children;
      extra = `obj3D children=${Array.isArray(children) ? children.length : '?'}`;
    }
    out.push({ key, type: ctorName, extra });
  }
  return out;
};

export const trackPostDispose = (
  label: string,
  target: object | null | undefined,
  meta?: Record<string, unknown>,
): void => {
  if (!enabled || !target) return;
  if (typeof WeakRef === 'undefined') {
    // Older browser — log a one-time note instead of probing.
    logEvent(`retention probe unavailable for ${label}`, { ...(meta ?? {}) });
    return;
  }
  const ref = new WeakRef(target);
  const startedAt = isBrowser ? performance.now() : 0;
  for (const delay of RETENTION_PROBE_DELAYS_MS) {
    setTimeout(() => {
      const stillAlive = ref.deref();
      const ageMs = isBrowser ? +(performance.now() - startedAt).toFixed(0) : delay;
      const isLastProbe = delay === RETENTION_PROBE_DELAYS_MS[RETENTION_PROBE_DELAYS_MS.length - 1];
      logEvent(`retention probe: ${label}`, {
        ageMs,
        stillAlive: stillAlive !== undefined,
        // Light-touch identification of the held object (constructor name only,
        // no deep traversal — must stay GC-neutral).
        type: stillAlive
          ? ((stillAlive as { constructor?: { name?: string } }).constructor?.name ?? 'unknown')
          : null,
        // On the FINAL probe of a still-alive target, dump its non-null field
        // summary — narrows down which property (and which type) is pinning
        // the target. We hold the ref only briefly through this synchronous
        // function; the WeakRef itself does NOT pin the target.
        retainedFields:
          stillAlive && isLastProbe ? summarizeRetainedFields(stillAlive) : undefined,
        ...(meta ?? {}),
      });
    }, delay);
  }
};

const initPageLifecycleListeners = (): void => {
  if (!enabled || !isBrowser) return;
  window.addEventListener('pagehide', (event) => {
    // Last log before potential OOM reload. If page is killed, persisted=false.
    logEvent('pagehide', { persisted: event.persisted });
  });
  window.addEventListener('visibilitychange', () => {
    logEvent('visibilitychange', { hidden: document.hidden });
  });
};

const bootstrap = (): void => {
  if (!enabled || !isBrowser) return;
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', ensureOverlay, { once: true });
  } else {
    ensureOverlay();
  }
  initPageLifecycleListeners();
  // Periodic snapshot so we have a time series even if no events fire.
  snapshotIntervalId = setInterval(() => {
    logEvent('periodic snapshot');
  }, 2000);
};

bootstrap();
